<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.elements.email.link.title')"
    :options="dialogOptions"
    :valid="validate"
  >
    <div class="mt-3 space-y-4 text-left">
      <o-field-group
        v-if="definedLinks.length"
        :label="$t('components.elements.email.link.definedLink')"
        class="block"
      >
        <select-field v-model="selectedLink" :options="definedLinkOptions" />
      </o-field-group>

      <o-field-group :label="$t('components.elements.editor.actions.link.linkUrl')" class="block">
        <text-field v-model="link.href" />
      </o-field-group>

      <o-field-group :label="$t('components.elements.editor.actions.link.linkText')" class="block">
        <text-field v-model="link.text" />
      </o-field-group>

      <o-field-group :label="$t('components.elements.editor.actions.link.element')" class="block">
        <segment-field
          v-model="link.element"
          class="border"
          :options="[
            { text: $t('general.link'), value: 'link' },
            { text: $t('components.elements.editor.actions.link.button'), value: 'button' },
          ]"
        />
      </o-field-group>

      <p v-if="link.href && !safeHref" class="text-xs text-red-600 dark:text-red-400">
        {{ $t('components.elements.email.link.invalidUrl') }}
      </p>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  SegmentField,
  SelectField,
  TextField,
} from '@/components/fields'
import { isSafeEmailLink, normaliseEmailEditorLinks } from '@/lib/emailEditor'

interface Props {
  links?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  links: () => [],
})

const { $i18n } = useNuxtApp()

const dialog = ref<any>()
const editor = shallowRef<any>(null)
const selection = ref({ from: 0, to: 0 })
const editingMark = ref<string | null>(null)
const selectedLink = ref('custom')
const selectingProgrammatically = ref(false)
const link = reactive({
  href: '',
  text: '',
  element: 'link',
})

const definedLinks = computed(() => normaliseEmailEditorLinks(props.links))
const definedLinkOptions = computed(() => [
  { text: $i18n.t('general.custom'), value: 'custom' },
  ...definedLinks.value.map((item, index) => ({ text: item.label, value: String(index) })),
])
const safeHref = computed(() => isSafeEmailLink(link.href))
const isValid = computed(() => Boolean(link.text.trim()) && safeHref.value)

const dialogOptions = computed(() => [
  { text: $i18n.t('general.cancel'), type: 'cancel' },
  ...(editingMark.value
    ? [{
        text: $i18n.t('general.remove'),
        type: 'confirm',
        variant: 'danger',
        flat: true,
        callback: remove,
        validate: false,
      }]
    : []),
  {
    text: $i18n.t('general.confirm'),
    type: 'confirm',
    callback: confirm,
    disabled: !isValid.value,
  },
])

watch(selectedLink, (value) => {
  if (selectingProgrammatically.value || value === 'custom') return

  const selected = definedLinks.value[Number(value)]
  if (!selected) return

  link.href = selected.href
  if (selected.text) link.text = selected.text
})

function open (editorInstance: any) {
  editor.value = editorInstance
  editingMark.value = editorInstance.isActive('emailButton')
    ? 'emailButton'
    : editorInstance.isActive('link')
      ? 'link'
      : null

  if (editingMark.value) {
    editorInstance.chain().focus().extendMarkRange(editingMark.value).run()
  }

  const { from, to } = editorInstance.state.selection
  selection.value = { from, to }

  const attrs = editingMark.value ? editorInstance.getAttributes(editingMark.value) : {}
  link.href = attrs.href ?? ''
  link.text = editorInstance.state.doc.textBetween(from, to, '')
  link.element = editingMark.value === 'emailButton' ? 'button' : 'link'

  selectingProgrammatically.value = true
  const matchedIndex = definedLinks.value.findIndex(item => item.href === link.href)
  selectedLink.value = matchedIndex === -1 ? 'custom' : String(matchedIndex)
  nextTick(() => { selectingProgrammatically.value = false })

  dialog.value.open()
}

function restoreSelection () {
  editor.value.commands.setTextSelection(selection.value)
}

function confirm () {
  if (!editor.value || !isValid.value) return false

  restoreSelection()

  const { from, to } = selection.value
  editor.value.view.dispatch(editor.value.state.tr.insertText(link.text, from, to))

  const end = from + link.text.length
  const chain = editor.value
    .chain()
    .focus()
    .setTextSelection({ from, to: end })
    .unsetLink()
    .unsetEmailButton()

  if (link.element === 'button') {
    chain.setEmailButton({ href: link.href })
  } else {
    chain.setLink({
      href: link.href,
      target: '_blank',
      rel: 'noopener noreferrer',
    })
  }

  chain.setTextSelection(end).run()
  return true
}

function remove () {
  if (!editor.value) return false

  restoreSelection()
  editor.value.chain().focus().unsetLink().unsetEmailButton().run()
  return true
}

function validate () {
  return isValid.value
}

defineExpose({ open })
</script>
